// src/pages/HuntComplete.tsx
import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ReactCanvasConfetti from 'react-canvas-confetti'
import { BunnyCharacter } from '../components/BunnyCharacter'
import { Button } from '../components/ui/Button'
import { api } from '../api/client'
import { SoundManager } from '../lib/sounds'
import type { Hunt } from '../types'

export function HuntComplete() {
  const { huntId } = useParams<{ huntId: string }>()
  const navigate = useNavigate()
  const [hunt, setHunt] = useState<Hunt | null>(null)

  useEffect(() => {
    if (huntId) {
      api.hunts.get(huntId).then(({ hunt }) => setHunt(hunt))
      // Mark hunt as completed
      api.hunts.update(huntId, { status: 'completed' })
      SoundManager.play('celebration')
    }
  }, [huntId])

  const fireConfetti = useCallback((confetti: any) => {
    if (!confetti) return
    // Fire multiple bursts
    const fire = () => {
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { x: Math.random(), y: Math.random() * 0.5 + 0.2 },
        colors: ['#FF6B6B', '#4ECDC4', '#FFE66D', '#A8E6CF', '#DDA0DD'],
      })
    }
    fire()
    setTimeout(fire, 300)
    setTimeout(fire, 600)
    setTimeout(fire, 1000)
  }, [])

  return (
    <div className="flex flex-col items-center justify-center min-h-dvh p-6 bg-cream relative overflow-hidden">
      <ReactCanvasConfetti
        onInit={({ confetti }) => fireConfetti(confetti)}
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
        }}
      />

      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 150, delay: 0.3 }}
      >
        <BunnyCharacter size="lg" animate="celebrate" />
      </motion.div>

      <motion.h1
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="font-heading text-4xl text-coral mt-6 text-center"
      >
        Ägg-stra fantastiskt!
      </motion.h1>

      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1 }}
        className="bg-white rounded-3xl p-6 mt-6 max-w-sm shadow-xl border border-turquoise/20"
      >
        <p className="font-body text-lg text-ink text-center leading-relaxed">
          {hunt?.story_finale ||
            'Tack för att ni hjälpte mig hitta alla mina ägg! Ni är de bästa äggjägarna i hela världen! Vid mina långa öron, jag är så glad!'}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="mt-8 pb-[env(safe-area-inset-bottom,0px)]"
      >
        <Button size="lg" variant="secondary" onClick={() => navigate('/')}>
          Tillbaka till start
        </Button>
      </motion.div>
    </div>
  )
}
